// src/App.js
import React, { Suspense, useCallback, useEffect } from 'react';
import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTour } from './contexts/TourContext';
import { useOnboarding } from './contexts/OnboardingContext';
import { CircularProgress, Box } from '@mui/material';
import './App.css';

// Core components - always loaded
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import RequireSystemAdmin from './components/RequireSystemAdmin';
import MfaSetupGate from './components/MfaSetupGate';
import PostLoginRouteSettled from './components/PostLoginRouteSettled';
import RequireAdmin from './components/RequireAdmin';
import RequireAdminOrManager from './components/RequireAdminOrManager';
import MyAppBar from './components/MyAppBar';
import ViewAsBanner from './components/ViewAsBanner';
import LandingPage from './components/LandingPage';
import AppLayout from './layouts/AppLayout';
import TaskFilterBar, { EMPTY_TASK_FILTERS } from './components/TaskFilterBar';
import FeatureGuard from './components/FeatureGuard';
import ErrorBoundary from './components/ErrorBoundary';
import { FEATURES } from './utils/featureGating';

// Retry wrapper for lazy imports — handles stale chunk errors after deployments
function lazyWithRetry(importFn) {
  return React.lazy(() =>
    importFn().catch(() => {
      // First import failed (likely stale chunk) — retry once
      return importFn();
    })
  );
}

// Lazy load individual settings components with retry
const BadgeLanding = lazyWithRetry(() => import('./components/BadgeLanding'));
// Loaded on demand (#902): they were in the first bundle, so a visitor to
// the landing page downloaded the task list and the dashboard with it.
const Dashboard = lazyWithRetry(() => import('./components/Dashboard'));
const TaskList = lazyWithRetry(() => import('./components/TaskList'));
const StartTrialForm = lazyWithRetry(() => import('./components/StartTrialForm'));
const SubscriberSelect = lazyWithRetry(() => import('./components/SubscriberSelect'));
const SettingsOverview = lazyWithRetry(() => import('./components/SettingsOverview'));
const SettingsCustomers = lazyWithRetry(() => import('./components/SettingsCustomers'));
const SettingsOperators = lazyWithRetry(() => import('./components/SettingsOperators'));
const SettingsUsers = lazyWithRetry(() => import('./components/SettingsUsers'));
const SettingsLocations = lazyWithRetry(() => import('./components/SettingsLocations'));
const SettingsLabelStocks = lazyWithRetry(() => import('./components/SettingsLabelStocks'));
const SettingsTaskTemplates = lazyWithRetry(() => import('./components/SettingsTaskTemplates'));
const SettingsEmailTemplates = lazyWithRetry(() => import('./components/SettingsEmailTemplates'));
const SettingsPDFTemplates = lazyWithRetry(() => import('./components/SettingsPDFTemplates'));
const SettingsScheduledReports = lazyWithRetry(() => import('./components/SettingsScheduledReports'));
const SettingsSLA = lazyWithRetry(() => import('./components/SettingsSLA'));
const SettingsFeatures = lazyWithRetry(() => import('./components/SettingsFeatures'));
const SettingsSecurity = lazyWithRetry(() => import('./components/SettingsSecurity'));
const SettingsApiKeys = lazyWithRetry(() => import('./components/SettingsApiKeys'));
const SettingsIntegrations = lazyWithRetry(() => import('./components/SettingsIntegrations'));
const SettingsSSO = lazyWithRetry(() => import('./components/SettingsSSO'));
const MfaEnrollment = lazyWithRetry(() => import('./components/MfaEnrollment'));
const ForcePasswordReset = lazyWithRetry(() => import('./components/ForcePasswordReset'));
const SettingsUsage = lazyWithRetry(() => import('./components/SettingsStorageManagement'));
const SettingsAbout = lazyWithRetry(() => import('./components/SettingsAbout'));
const WhatsNew = lazyWithRetry(() => import('./components/WhatsNew'));
const SettingsUserGuide = lazyWithRetry(() => import('./components/SettingsUserGuide'));
const SettingsAssetTemplates = lazyWithRetry(() => import('./components/SettingsAssetTemplates'));
const AuthAction = lazyWithRetry(() => import('./components/AuthAction'));
const SetPassword = lazyWithRetry(() => import('./components/SetPassword'));
const SettingsDownloads = lazyWithRetry(() => import('./components/SettingsDownloads'));
const SubscriberAuditLog = lazyWithRetry(() => import('./components/SubscriberAuditLog'));
const SettingsAccessMatrix = lazyWithRetry(() => import('./components/SettingsAccessMatrix'));
const CreateTemplateWizard = lazyWithRetry(() => import('./components/CreateTemplateWizard'));

// Lazy load assets/issues/recyclebin/search
const Assets = lazyWithRetry(() => import('./components/Assets'));
const AssetDetailPage = lazyWithRetry(() => import('./components/AssetDetailPage'));
const Assignments = lazyWithRetry(() => import('./components/Assignments'));
const WorkspaceAnalytics = lazyWithRetry(() => import('./components/WorkspaceAnalytics'));
const Issues = lazyWithRetry(() => import('./components/Issues'));
const IssuesTestPage = lazyWithRetry(() => import('./components/IssuesTestPage'));
const RecycleBin = lazyWithRetry(() => import('./components/RecycleBin'));
const AdvancedSearch = lazyWithRetry(() => import('./components/AdvancedSearch'));
const TaskDetailPage = lazyWithRetry(() => import('./components/TaskDetailPage'));
const TaskIdHistoryPage = lazyWithRetry(() => import('./components/TaskIdHistoryPage'));

// Lazy load admin components
const SystemAdminDashboard = lazyWithRetry(() => import('./components/SystemAdminDashboard'));
const SystemAdminManagement = lazyWithRetry(() => import('./components/SystemAdminManagement'));
const SystemAdminSubscribers = lazyWithRetry(() => import('./components/SystemAdminSubscribers'));
const SystemAdminLiveTasks = lazyWithRetry(() => import('./components/SystemAdminLiveTasks'));
const SystemAdminAnalytics = lazyWithRetry(() => import('./components/SystemAdminAnalytics'));
const SystemAdminPlans = lazyWithRetry(() => import('./components/SystemAdminPlans'));
const SystemAdminStorage = lazyWithRetry(() => import('./components/SystemAdminStorage'));
const SystemAdminAuditLog = lazyWithRetry(() => import('./components/SystemAdminAuditLog'));
const SystemAdminSignupAttempts = lazyWithRetry(() => import('./components/SystemAdminSignupAttempts'));
const SystemAdminSignInAttempts = lazyWithRetry(() => import('./components/SystemAdminSignInAttempts'));
const SystemAdminAppUpdates = lazyWithRetry(() => import('./components/SystemAdminAppUpdates'));
const SystemAdminEmailLog = lazyWithRetry(() => import('./components/SystemAdminEmailLog'));
const SystemAdminSignupTokens = lazyWithRetry(() => import('./components/SystemAdminSignupTokens'));
const SystemAdminMigration = lazyWithRetry(() => import('./components/SystemAdminMigration'));
const SystemAdminAISettings = lazyWithRetry(() => import('./components/SystemAdminAISettings'));
const SystemAdminMcp = lazyWithRetry(() => import('./components/SystemAdminMcp'));
const SystemAdminBilling = lazyWithRetry(() => import('./components/SystemAdminBilling'));
const SystemAdminUsageCheck = lazyWithRetry(() => import('./components/SystemAdminUsageCheck'));
const SystemAdminTemplates = lazyWithRetry(() => import('./components/SystemAdminTemplates'));
const TemplateTranslations = lazyWithRetry(() => import('./components/TemplateTranslations'));

// Lazy load public components (no authentication required)
const PublicIssueReport = lazyWithRetry(() => import('./components/PublicIssueReport'));
const PrivacyPolicy = lazyWithRetry(() => import('./components/PrivacyPolicy'));

// Lazy load customer portal component
const CustomerPortal = lazyWithRetry(() => import('./components/CustomerPortal'));
const SharedTaskPage = lazyWithRetry(() => import('./components/SharedTaskPage'));

// Loading component
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
    <CircularProgress />
  </Box>
);

// Placeholder for pages that will use AppLayout
const TasksPage = ({ currentAppFilters, onFiltersChange }) => {
  const [mode, setMode] = useState('live'); // 'live' or 'dateRange' - using live for Firestore data
  const { subscriberId } = useAuth();
  const { tourCompleted, tourRequested, startTour, tourActive } = useTour();
  const { shouldShowOnboarding } = useOnboarding();
  // The filter bar reports "N of M match"; the counts are only known once the
  // list has loaded and applied its client-side slice.
  const [counts, setCounts] = useState({ shown: null, total: null });
  // Resolved server-side in the workspace's timezone and reported up by the
  // list, so the bar can show the window it actually got rather than deriving
  // one from the browser's clock.
  const [dateWindow, setDateWindow] = useState(null);
  const handleCountsChange = useCallback(({ shown, total }) => {
    setCounts(prev => (prev.shown === shown && prev.total === total ? prev : { shown, total }));
  }, []);
  // Bumped whenever the list changes a task's status or removes one, so the
  // view counts on the filter bar are re-read rather than left at whatever
  // they were when the page opened.
  const [tasksChangedTick, setTasksChangedTick] = useState(0);
  const handleTasksChanged = useCallback(() => setTasksChangedTick(t => t + 1), []);
  const clearFilters = useCallback(() => {
    const today = new Date();
    onFiltersChange({ ...EMPTY_TASK_FILTERS, dateFrom: today, dateTo: today });
  }, [onFiltersChange]);

  // Auto-start tour for new users on first visit
  useEffect(() => {
    // Starts by itself in two cases only: the workspace is still being set up
    // (the getting-started checklist is showing), or someone asked for it from
    // Settings, User Guide. `tourCompleted` alone is not enough, because it is
    // also false for every admin who simply never took the tour, and until
    // 2026-09-21 that was every admin: the tour state never loaded at all (see
    // TourContext). Gating on it alone would have started the tour on the next
    // visit of every established workspace.
    if (subscriberId && tourCompleted === false && !tourActive && (tourRequested || shouldShowOnboarding)) {
      const timer = setTimeout(() => startTour(), 800);
      return () => clearTimeout(timer);
    }
  }, [subscriberId, tourCompleted, tourRequested, shouldShowOnboarding, tourActive, startTour]);

  return (
    <>
      <TaskFilterBar
        currentFilters={currentAppFilters}
        onFilterChange={onFiltersChange}
        mode={mode}
        onModeChange={setMode}
        subscriberId={subscriberId}
        matchingCount={counts.shown}
        totalCount={counts.total}
        dateWindow={dateWindow}
        countsRefreshKey={tasksChangedTick}
      />
      <Suspense fallback={<LoadingFallback />}>
        <TaskList
          currentAppFilters={currentAppFilters}
          mode={mode}
          onCountsChange={handleCountsChange}
          onWindowChange={setDateWindow}
          onClearFilters={clearFilters}
          onTasksChanged={handleTasksChanged}
        />
      </Suspense>
    </>
  );
};


// Component to handle root route logic
const HomeRoute = () => {
  const { currentUser, loading, userRole, subscriberId } = useAuth();

  if (loading) {
    return <div style={{textAlign: 'center', marginTop: '20%'}}>Loading...</div>;
  }

  // If user is logged in, redirect based on role and subscriber state
  if (currentUser) {
    if (userRole === 'customer') {
      return <Navigate to="/customer-portal" replace />;
    }
    if (!subscriberId) {
      return <Navigate to="/select-subscriber" replace />;
    }
    return <Navigate to="/tasks" replace />;
  }

  // If not logged in, show landing page
  return <LandingPage />;
};


function AppContent() {
  const { currentUser, loading, logout, userRole, subscriberId, postLoginInProgress } = useAuth();
  const [appFilters, setAppFilters] = useState({}); // Initial filters state

  // This function will be passed to AppLayout, which passes it to TaskFilters
  const handleFiltersChange = (newFilters) => {
    console.log("App-level filters changed:", newFilters);
    setAppFilters(newFilters);
    // Here you would also manage fetching the current subscriberId if it's not part of newFilters directly
    // For example, if Dashboard sets the selected subscriber, that ID needs to be part of appFilters
  };


  if (loading) {
    return <div style={{textAlign: 'center', marginTop: '20%'}}>Loading Application...</div>;
  }

  return (
    <Router>
      <MyAppBar currentUser={currentUser} onLogout={logout} />
      <ViewAsBanner />
      <MfaSetupGate />
      <PostLoginRouteSettled />
      <div className="main-content-area"> {/* Optional wrapper for content below AppBar */}
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={currentUser && !postLoginInProgress ? (userRole === 'customer' ? <Navigate to="/customer-portal" replace /> : (subscriberId ? <Navigate to="/tasks" replace /> : <Navigate to="/select-subscriber" replace />)) : <Login />} />
          <Route path="/select-subscriber" element={<Suspense fallback={<LoadingFallback />}><SubscriberSelect /></Suspense>} />
          <Route path="/enroll-mfa" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><MfaEnrollment /></Suspense></ProtectedRoute>} />
          <Route path="/force-password-reset" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><ForcePasswordReset /></Suspense></ProtectedRoute>} />
          <Route path="/" element={<HomeRoute />} />
          
          {/* Public Routes - No Authentication Required */}
          <Route
            path="/r/:code"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PublicIssueReport />
              </Suspense>
            }
          />
          {/* Operator badge landing. Reached only when the app is NOT
              installed — otherwise Android App Links open the app directly and
              the browser never loads this. The component deliberately never
              reads the :payload segment; see BadgeLanding.js. */}
          <Route
            path="/b/:payload"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <BadgeLanding />
              </Suspense>
            }
          />
          <Route path="/start-trial" element={<Suspense fallback={<LoadingFallback />}><StartTrialForm /></Suspense>} />
          {/* Public by design: the recipient of a share link has no account,
              which is the whole point of an ad-hoc share as opposed to portal
              access. Outside ProtectedRoute, and never redirected to /login —
              a signed-in admin opening the link should see the page too. */}
          <Route path="/shared/:token" element={<Suspense fallback={<LoadingFallback />}><SharedTaskPage /></Suspense>} />
          <Route path="/auth/action" element={<Suspense fallback={<LoadingFallback />}><AuthAction /></Suspense>} />
          {/* Public: the durable half of the invitation flow. A migrated user
              whose emailed reset link has expired mints a fresh one here. */}
          <Route path="/set-password" element={<Suspense fallback={<LoadingFallback />}><SetPassword /></Suspense>} />
          <Route path="/whats-new" element={<Suspense fallback={<LoadingFallback />}><WhatsNew /></Suspense>} />
          <Route
            path="/privacy-policy"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PrivacyPolicy />
              </Suspense>
            }
          />

          {/* Customer Portal Route */}
          <Route
            path="/customer-portal"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <CustomerPortal />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Authenticated Routes using AppLayout */}
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <TasksPage currentAppFilters={appFilters} onFiltersChange={handleFiltersChange} />
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {/* Before /tasks/:taskId, or "by-id" is read as a task id. */}
          <Route
            path="/tasks/by-id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingFallback />}>
                      <TaskIdHistoryPage />
                    </Suspense>
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/:taskId"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingFallback />}>
                      <TaskDetailPage />
                    </Suspense>
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
           <Route
            path="/dashboard" // We can keep dashboard to select subscriber
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <Suspense fallback={<LoadingFallback />}>
                    <Dashboard setAppSubscriberFilter={(subscriberId) => handleFiltersChange({ ...appFilters, subscriberId: subscriberId || null })} />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            // Was a "Templates Page (TODO)" placeholder nothing linked to.
            element={<Navigate to="/settings/task-templates" replace />}
          />
          <Route
            path="/users"
            // Was a "Users Page (TODO)" placeholder nothing linked to.
            element={<Navigate to="/settings/users" replace />}
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <SettingsOverview />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/settings/customers" element={<ProtectedRoute><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsCustomers /></Suspense></AppLayout></ProtectedRoute>} />
          <Route path="/settings/operators" element={<ProtectedRoute><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsOperators /></Suspense></AppLayout></ProtectedRoute>} />
          <Route path="/settings/users" element={<ProtectedRoute><RequireAdmin><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsUsers /></Suspense></AppLayout></RequireAdmin></ProtectedRoute>} />
          <Route path="/settings/locations" element={<ProtectedRoute><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsLocations /></Suspense></AppLayout></ProtectedRoute>} />
          <Route path="/settings/label-stocks" element={<ProtectedRoute><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsLabelStocks /></Suspense></AppLayout></ProtectedRoute>} />
          <Route path="/settings/task-templates" element={<ProtectedRoute><RequireAdminOrManager><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsTaskTemplates /></Suspense></AppLayout></RequireAdminOrManager></ProtectedRoute>} />
          <Route path="/settings/asset-templates" element={<ProtectedRoute><RequireAdminOrManager><AppLayout><FeatureGuard feature={FEATURES.ASSET_TRACKING}><Suspense fallback={<LoadingFallback />}><SettingsAssetTemplates /></Suspense></FeatureGuard></AppLayout></RequireAdminOrManager></ProtectedRoute>} />
          <Route path="/settings/email-templates" element={<ProtectedRoute><RequireAdminOrManager><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsEmailTemplates /></Suspense></AppLayout></RequireAdminOrManager></ProtectedRoute>} />
          <Route path="/settings/pdf-templates" element={<ProtectedRoute><RequireAdminOrManager><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsPDFTemplates /></Suspense></AppLayout></RequireAdminOrManager></ProtectedRoute>} />
          <Route path="/settings/scheduled-reports" element={<ProtectedRoute><RequireAdminOrManager><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsScheduledReports /></Suspense></AppLayout></RequireAdminOrManager></ProtectedRoute>} />
          <Route path="/settings/sla" element={<ProtectedRoute><RequireAdminOrManager><AppLayout><FeatureGuard feature={FEATURES.ISSUE_TRACKING}><Suspense fallback={<LoadingFallback />}><SettingsSLA /></Suspense></FeatureGuard></AppLayout></RequireAdminOrManager></ProtectedRoute>} />
          <Route path="/settings/features" element={<ProtectedRoute><RequireAdminOrManager><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsFeatures /></Suspense></AppLayout></RequireAdminOrManager></ProtectedRoute>} />
          <Route path="/settings/security" element={<ProtectedRoute><RequireAdmin><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsSecurity /></Suspense></AppLayout></RequireAdmin></ProtectedRoute>} />
          <Route path="/settings/sso" element={<ProtectedRoute><RequireAdmin><AppLayout><FeatureGuard feature={FEATURES.SSO}><Suspense fallback={<LoadingFallback />}><SettingsSSO /></Suspense></FeatureGuard></AppLayout></RequireAdmin></ProtectedRoute>} />
          <Route path="/settings/api-keys" element={<ProtectedRoute><RequireAdmin><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsApiKeys /></Suspense></AppLayout></RequireAdmin></ProtectedRoute>} />
          <Route path="/settings/integrations" element={<ProtectedRoute><RequireAdmin><AppLayout><FeatureGuard feature={FEATURES.INTEGRATIONS}><Suspense fallback={<LoadingFallback />}><SettingsIntegrations /></Suspense></FeatureGuard></AppLayout></RequireAdmin></ProtectedRoute>} />
          <Route path="/settings/usage" element={<ProtectedRoute><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsUsage /></Suspense></AppLayout></ProtectedRoute>} />
          <Route path="/settings/downloads" element={<ProtectedRoute><AppLayout><FeatureGuard feature={FEATURES.MASS_EXPORT}><Suspense fallback={<LoadingFallback />}><SettingsDownloads /></Suspense></FeatureGuard></AppLayout></ProtectedRoute>} />
          <Route path="/settings/about" element={<ProtectedRoute><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsAbout /></Suspense></AppLayout></ProtectedRoute>} />
          <Route path="/settings/user-guide" element={<ProtectedRoute><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsUserGuide /></Suspense></AppLayout></ProtectedRoute>} />
          <Route path="/settings/audit-log" element={<ProtectedRoute><RequireAdminOrManager><AppLayout><Suspense fallback={<LoadingFallback />}><SubscriberAuditLog /></Suspense></AppLayout></RequireAdminOrManager></ProtectedRoute>} />
          <Route path="/settings/access-matrix" element={<ProtectedRoute><RequireAdmin><AppLayout><Suspense fallback={<LoadingFallback />}><SettingsAccessMatrix /></Suspense></AppLayout></RequireAdmin></ProtectedRoute>} />
          <Route
            path="/assets"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <FeatureGuard feature={FEATURES.ASSET_TRACKING}>
                    <Suspense fallback={<LoadingFallback />}>
                      <Assets />
                    </Suspense>
                  </FeatureGuard>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets/:assetId"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <FeatureGuard feature={FEATURES.ASSET_TRACKING}>
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetDetailPage />
                    </Suspense>
                  </FeatureGuard>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/issues"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <FeatureGuard feature={FEATURES.ISSUE_TRACKING}>
                    <Suspense fallback={<LoadingFallback />}>
                      <Issues />
                    </Suspense>
                  </FeatureGuard>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assignments"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <FeatureGuard feature={FEATURES.ASSIGNMENTS}>
                    <Suspense fallback={<LoadingFallback />}>
                      <Assignments />
                    </Suspense>
                  </FeatureGuard>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {/* Pro analytics dashboard: admin / manager, and the system-admin-set
              advanced_analytics flag. The API enforces both again. */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <RequireAdminOrManager>
                  <AppLayout>
                    <FeatureGuard feature={FEATURES.ADVANCED_ANALYTICS}>
                      <Suspense fallback={<LoadingFallback />}>
                        <WorkspaceAnalytics />
                      </Suspense>
                    </FeatureGuard>
                  </AppLayout>
                </RequireAdminOrManager>
              </ProtectedRoute>
            }
          />
          <Route
            path="/issues-test"
            element={
              // System admins only. The page writes test issues into the
              // live issues collection, which mail and push real teams; any
              // signed-in member of a workspace with issue tracking could
              // open it (#902).
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <AppLayout>
                    <FeatureGuard feature={FEATURES.ISSUE_TRACKING}>
                      <Suspense fallback={<LoadingFallback />}>
                        <IssuesTestPage />
                      </Suspense>
                    </FeatureGuard>
                  </AppLayout>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingFallback />}>
                      <AdvancedSearch />
                    </Suspense>
                  </ErrorBoundary>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recycle-bin"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <RecycleBin />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/downloads"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <FeatureGuard feature={FEATURES.MASS_EXPORT}>
                    <Suspense fallback={<LoadingFallback />}>
                      <SettingsDownloads />
                    </Suspense>
                  </FeatureGuard>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/create"
            element={
              <ProtectedRoute>
                <RequireAdminOrManager>
                  <Suspense fallback={<LoadingFallback />}>
                    <CreateTemplateWizard />
                  </Suspense>
                </RequireAdminOrManager>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/edit/:templateId"
            element={
              <ProtectedRoute>
                <RequireAdminOrManager>
                  <Suspense fallback={<LoadingFallback />}>
                    <CreateTemplateWizard mode="edit" />
                  </Suspense>
                </RequireAdminOrManager>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/:templateId/translations"
            element={
              <ProtectedRoute>
                <RequireAdminOrManager>
                  <AppLayout>
                    <Suspense fallback={<LoadingFallback />}>
                      <TemplateTranslations />
                    </Suspense>
                  </AppLayout>
                </RequireAdminOrManager>
              </ProtectedRoute>
            }
          />

          {/* System Admin Routes */}
          <Route
            path="/system-admin"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminDashboard />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/admins"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminManagement />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/subscribers"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminSubscribers />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/live-tasks"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminLiveTasks />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/analytics"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminAnalytics />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/plans"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminPlans />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/billing"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminBilling />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/templates"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminTemplates />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/usage-check"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminUsageCheck />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/storage"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminStorage />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/audit-log"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminAuditLog />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/signup-attempts"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminSignupAttempts />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/sign-in-attempts"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminSignInAttempts />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/app-updates"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminAppUpdates />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/email-log"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminEmailLog />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/signup-tokens"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminSignupTokens />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/migration"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminMigration />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/ai-settings"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminAISettings />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-admin/mcp"
            element={
              <ProtectedRoute>
                <RequireSystemAdmin>
                  <Suspense fallback={<LoadingFallback />}>
                    <SystemAdminMcp />
                  </Suspense>
                </RequireSystemAdmin>
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<div style={{padding: '20px'}}><h2>404 Page Not Found</h2><Link to="/">Go Home</Link></div>} />
        </Routes>
        </ErrorBoundary>
      </div>
    </Router>
  );
}

// App.js still just renders AppContent wrapped by providers in index.js
function App() {
  return <AppContent />;
}
// Need to import useState in App.js for appFilters
// Add this at the top of App.js

export default App;